import React from 'react';
import { ChevronRight, Package2, Search, Truck, FileText, Shield, Clock, Users } from 'lucide-react';
import RequestForm from './services/RequestForm';

function ServicesPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B3BA0]/50 via-[#1B3BA0]/30 to-transparent mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative text-center">
          <div className="max-w-[780px] mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">Комплексные решения</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                для вашего бизнеса
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed mx-auto">
              Полный спектр услуг по работе с Китаем: от поиска поставщиков до доставки товара на ваш склад
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Search className="w-12 h-12 text-[#1B3BA0]" />,
                title: 'Поиск и выкуп товаров',
                description: 'Профессиональный поиск поставщиков и товаров на китайских площадках. Проверка качества и надёжности производителей.',
                link: '/services/business/search',
                features: ['Поиск поставщиков', 'Проверка производителей', 'Выкуп образцов', 'Контроль качества']
              },
              {
                icon: <Truck className="w-12 h-12 text-[#1B3BA0]" />,
                title: 'Логистика и таможня',
                description: 'Комплексные решения по доставке грузов из Китая с таможенным оформлением и страхованием.',
                link: '/services/business/logistics',
                features: ['Авто доставка', 'Таможенное оформление', 'Страхование грузов', 'Складское хранение']
              },
              {
                icon: <FileText className="w-12 h-12 text-[#1B3BA0]" />,
                title: 'Экспедирование',
                description: 'Контроль отгрузки и проверка качества товара перед отправкой. Инспекция производства и упаковки.',
                link: '/services/business/forwarding',
                features: ['Инспекция производства', 'Контроль качества', 'Проверка упаковки', 'Фото отчёты']
              },
              {
                icon: <Shield className="w-12 h-12 text-[#1B3BA0]" />,
                title: 'Оплата поставщикам',
                description: 'Безопасные способы оплаты китайским поставщикам. Защита сделок и возврат платежей при необходимости.',
                link: '/services/business/payment',
                features: ['Безопасные платежи', 'Возврат средств', 'Конвертация валют', 'Работа по договору']
              }
            ].map((service, index) => (
              <a 
                key={index}
                href={service.link}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group border border-gray-100"
              >
                <div className="bg-[#1B3BA0]/5 rounded-2xl p-4 inline-block mb-6">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-semibold text-[#1d1d1f] mb-4 flex items-center group-hover:text-[#1B3BA0]">
                  {service.title}
                  <ChevronRight className="w-5 h-5 ml-2 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                </h3>
                <p className="text-[#86868b] mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-[#1d1d1f]">
                      <Clock className="w-4 h-4 text-[#1B3BA0] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-6 bg-[#f5f5f7]">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            Почему выбирают нас
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Users className="w-16 h-16 text-[#1B3BA0]" />,
                title: 'Опытная команда',
                description: 'Более 10 лет опыта работы с Китаем и тысячи успешных поставок'
              },
              {
                icon: <Package2 className="w-16 h-16 text-[#1B3BA0]" />,
                title: 'Полный сервис',
                description: 'От поиска товара до доставки на ваш склад — всё в одном месте'
              },
              {
                icon: <Shield className="w-16 h-16 text-[#1B3BA0]" />,
                title: 'Гарантия качества',
                description: 'Проверка товаров, страхование грузов и возврат платежей'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="bg-[#1B3BA0]/5 rounded-full p-6 inline-block mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{feature.title}</h3>
                <p className="text-[#86868b]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Начните работать с нами
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Оставьте заявку, и мы свяжемся с вами для обсуждения вашего проекта
          </p>
          <RequestForm source="Услуги" />
        </div>
      </section>
    </div>
  );
}

export default ServicesPage;